import React, { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import PageWrapper from "../../../../components/layout/page/PageWrapper";
import Heading from "../../../../components/typography/Heading";
import Text from "../../../../components/typography/Text";
import TextField from "../../../../components/input/textfield/TextField";
import Button from "../../../../components/buttons/Button";
import StatusAlert from "../../../../components/alerts/StatusAlert";
import FeedbackStatus from "../../../../components/feedback/FeedbackStatus";

import { useResetPassword } from "../../hooks/useResetPassword";

import "./ResetPasswordPage.css";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const { userData, passwords, setPasswords, status, handleUpdatePassword } =
    useResetPassword(token, navigate);

  useEffect(() => {
    document.title = "Katch | Reset Password";
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!status.loading) {
      handleUpdatePassword(event);
    }
  };

  const handlePasswordChange = (field, value) => {
    setPasswords((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const isVerifying = status.loading && !userData?.success;

  return (
    <PageWrapper className="reset-password-wrapper">
      <main className="reset-password-page">
        <div className="reset-password-page__content">
          {isVerifying ? (
            <Text
              as="p"
              size="sm"
              tone="secondary"
              className="reset-password-page__loading"
            >
              Verifying secure session...
            </Text>
          ) : userData?.success ? (
            <>
              <div className="reset-password-page__header">
                <Heading as="h1" size="lg">
                  New password
                </Heading>

                <Text as="p" size="sm" tone="secondary">
                  Enter and confirm your new password.
                </Text>
              </div>

              <form
                className="reset-password-page__form"
                onSubmit={handleSubmit}
              >
                {status.message && <StatusAlert status={status} />}

                <div className="reset-password-page__fields">
                  <TextField
                    name="newPassword"
                    type="password"
                    label="New password"
                    value={passwords.new}
                    onChange={(event) =>
                      handlePasswordChange("new", event.target.value)
                    }
                    placeholder="Enter new password"
                    autoComplete="new-password"
                    showPasswordOption
                    required
                  />

                  <TextField
                    name="confirmPassword"
                    type="password"
                    label="Confirm password"
                    value={passwords.confirm}
                    onChange={(event) =>
                      handlePasswordChange("confirm", event.target.value)
                    }
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                    showPasswordOption
                    required
                  />
                </div>

                <Button
                  type="submit"
                  value={status.loading ? "Saving..." : "Save password"}
                  variant="primary"
                  disabled={status.loading}
                />
              </form>

              <div className="reset-password-page__footer">
                <Text as="p" size="sm" tone="secondary">
                  Remember your password?{" "}
                  <Link to="/sign-in" className="reset-password-page__link">
                    Sign in
                  </Link>
                </Text>
              </div>
            </>
          ) : (
            <FeedbackStatus
              title="Link expired"
              description="For security, password reset links expire. Request a new link to continue."
              buttonText="Request new link"
              onAction={() => navigate("/forgot-password")}
            />
          )}
        </div>
      </main>
    </PageWrapper>
  );
}
